import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import { useLayoutEffect, useMemo, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import Checkbox from 'core/elements/input/Checkbox'
import Text from 'core/elements/Text'
import useScopedPreferences from 'core/session/useScopedPreferences'
import type Theme from 'core/themes/model'
import FontAwesomeIcon from '../../components/FontAwesomeIcon'
import GridTableHeading, { pinColumnWidths } from './GridTableHeading'
import { ACTION_BAR_HEIGHT } from './gridConstants'
import type { GridManagedColumnsProps } from './hooks/useGridManagedColumns'
import type { GridBatchActionsProps, SelectableParsedGridRow } from './hooks/useGridSelectableRows'
import type { GridSortingProps } from './hooks/useGridSorting'

export interface GridHeaderProps<T>
  extends GridSortingProps,
    GridBatchActionsProps<T>,
    GridManagedColumnsProps {
  pageRows: Array<SelectableParsedGridRow<T>>
  rowMenuItemsLength: number
  hideCheckboxes: boolean
  isReadOnly?: boolean
  actionBarVisible?: boolean
  /** Grid was given a renderExpandedRow prop: add a matching empty header cell */
  hasExpandableRows?: boolean
}

interface SelectAllColumnProps<T> {
  className: string
  pageRows: Array<SelectableParsedGridRow<T>>
  rowsSelectionDisabled: boolean
  multiSelectionEnabled: boolean
  toggleSelectAll: (rows: Array<SelectableParsedGridRow<T>>) => void
}

interface GridHeaderStylesProps {
  actionBarVisible?: boolean
}

const useStyles = makeStyles<Theme, GridHeaderStylesProps>((theme) => ({
  gridHead: {
    border: 'none',
    position: 'sticky',
    top: ({ actionBarVisible }) => (actionBarVisible ? ACTION_BAR_HEIGHT : 0),
    zIndex: 9, // 1 more than the sticky row action
  },
  gridHeadTr: {
    textAlign: 'left',
  },
  gridHeadTitleTr: {
    backgroundColor: theme.components.table.hoverBackground,
    height: 36,
    border: 'none',
    // box-shadow, not border: with border-collapse + a sticky thead, the
    // collapsed border between this row and the (scrolled-away) rows below
    // it stops painting once the header detaches from normal flow on scroll.
    boxShadow: `inset 0 1px 0 ${theme.components.table.border}, inset 0 -1px 0 ${theme.components.table.border}`,
  },
  gridSelectAllTh: {
    width: 40,
    paddingLeft: 10,
  },
}))

function SelectAllColumn<T>({
  className,
  pageRows,
  rowsSelectionDisabled,
  multiSelectionEnabled,
  toggleSelectAll,
}: SelectAllColumnProps<T>) {
  if (rowsSelectionDisabled) {
    return null
  }
  // Reflect only the CURRENT page. The box is checked when every row on this
  // page is selected, and indeterminate when some (but not all) are. Deriving
  // this from the global selection status made the box show a dash on pages
  // with nothing selected whenever another page had a selection. SELECTROWS-FR-016.
  const checked = pageRows.length > 0 && pageRows.every(({ isSelected }) => isSelected)
  const indeterminate = !checked && pageRows.some(({ isSelected }) => isSelected)
  return (
    <th className={clsx(className, 'select-column')}>
      {multiSelectionEnabled ? (
        <Checkbox
          checked={checked || indeterminate}
          indeterminate={indeterminate}
          onChange={() => toggleSelectAll(pageRows)}
        />
      ) : null}
    </th>
  )
}

export default function GridHeader<T>(props: GridHeaderProps<T>) {
  const {
    columns,
    sortingDisabled,
    sortedBy,
    sortedDirection,
    toggleSort,
    multiSelectionEnabled,
    toggleSelectAll,
    rowsSelectionDisabled,
    pageRows,
    rowMenuItemsLength,
    hideCheckboxes,
    isReadOnly,
    actionBarVisible,
    hasExpandableRows,
  } = props
  // Stabilize identity across renders where the boolean doesn't change, so
  // react-jss's dynamic `top` rule isn't recomputed on every unrelated re-render.
  const stylesProps = useMemo(() => ({ actionBarVisible }), [actionBarVisible])
  const classes = useStyles(stylesProps)
  const theadRef = useRef<HTMLTableSectionElement>(null)
  // widths are saved per user and route, so every grid gets this with no consumer wiring
  const { pathname } = useLocation()
  const { prefs, updatePrefs } =
    useScopedPreferences<Record<string, Record<string, number>>>('gridColumnWidths')
  const savedWidths = prefs[pathname]
  const visibleKeys = columns
    .filter(({ visible }) => visible !== false)
    .map(({ key }) => key)
    .join()

  // Apply the saved widths, and after a resize the table width is pinned (see GridTableHeading).
  // When columns are added or removed (manage columns) that width is stale: unpin, let the
  // table re-lay out, pin again.
  useLayoutEffect(() => {
    const table = theadRef.current?.closest('table')
    if (!table || !(table.style.width || savedWidths)) {
      return
    }
    table.style.width = ''
    pinColumnWidths(table, Array.from(theadRef.current.querySelectorAll('th')), savedWidths)
    // savedWidths is deliberately not a dependency: a drag saves it and the DOM already has it
  }, [visibleKeys, hideCheckboxes, rowMenuItemsLength, isReadOnly, hasExpandableRows])

  return (
    <thead ref={theadRef} className={classes.gridHead} data-testid="grid-header--thead-0">
      <Text
        component="tr"
        variant="caption2"
        className={clsx(classes.gridHeadTr, classes.gridHeadTitleTr)}
      >
        {!hideCheckboxes && (
          <SelectAllColumn
            className={classes.gridSelectAllTh}
            {...{
              pageRows,
              rowsSelectionDisabled,
              multiSelectionEnabled,
              toggleSelectAll,
            }}
          />
        )}
        {columns
          .filter(({ visible }) => visible !== false)
          .map(({ key, width, tooltip, label, disableSorting: columnSortDisabled }) => {
            const isSortDisabled = sortingDisabled || columnSortDisabled
            return (
              <GridTableHeading
                width={width}
                resizable
                data-column-key={key}
                onResizeEnd={(widths) => updatePrefs({ [pathname]: widths })}
                sortingDisabled={isSortDisabled}
                onClick={isSortDisabled ? null : () => toggleSort(key)}
                key={key}
              >
                <Text
                  title={String(tooltip || label)}
                  variant="caption2"
                  className="grid_header-text"
                >
                  {label}
                </Text>
                {!isSortDisabled && sortedBy === key ? (
                  <FontAwesomeIcon aria-hidden="true" solid className="grid_header-direction">
                    {`sort-${sortedDirection === 'asc' ? 'up' : 'down'}`}
                  </FontAwesomeIcon>
                ) : null}
              </GridTableHeading>
            )
          })}
        {isReadOnly ? null : rowMenuItemsLength ? (
          <GridTableHeading width={20} sortingDisabled key="row-menu" className="row-menu" />
        ) : null}
        {hasExpandableRows ? (
          <GridTableHeading
            width={32}
            sortingDisabled
            key="expand-toggle"
            className="expand-toggle"
          />
        ) : null}
      </Text>
    </thead>
  )
}
